// @ts-nocheck
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, ArrowRightLeft } from "lucide-react";
import { useTransfers, useInventoryItems, useWarehouses, useUpcomingEvents } from "@/hooks/useInventoryData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500 text-white",
  shipped: "bg-blue-500 text-white",
  received: "bg-green-500 text-white",
  cancelled: "bg-gray-400 text-white",
};

const TransfersTab: React.FC = () => {
  const { data: transfers, isLoading } = useTransfers();
  const { data: items } = useInventoryItems();
  const { data: warehouses } = useWarehouses();
  const { data: events } = useUpcomingEvents();
  const queryClient = useQueryClient();

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterWarehouse, setFilterWarehouse] = useState("all");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({
    item_id: "", from_warehouse_id: "", to_warehouse_id: "", quantity: "1",
    related_event_id: "", ship_date: "", tracking_number: "", notes: "", reason: "",
  });

  const filtered = (transfers || []).filter((t: any) => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterWarehouse !== "all" && t.from_warehouse_id !== filterWarehouse && t.to_warehouse_id !== filterWarehouse) return false;
    return true;
  });

  const handleCreate = async () => {
    if (!form.item_id || !form.from_warehouse_id || !form.to_warehouse_id) {
      toast.error("Item, From, and To warehouse are required");
      return;
    }
    if (form.from_warehouse_id === form.to_warehouse_id) {
      toast.error("From and To warehouse must be different");
      return;
    }
    const { error } = await supabase.from("manage_inventory_transfers").insert({
      item_id: form.item_id,
      from_warehouse_id: form.from_warehouse_id,
      to_warehouse_id: form.to_warehouse_id,
      quantity: parseInt(form.quantity) || 1,
      related_event_id: form.related_event_id || null,
      ship_date: form.ship_date || null,
      tracking_number: form.tracking_number || null,
      notes: form.notes || null,
      reason: form.reason || null,
      status: "pending",
    });
    if (error) { toast.error("Failed to create transfer"); return; }
    toast.success("Transfer created");
    setNewOpen(false);
    setForm({ item_id: "", from_warehouse_id: "", to_warehouse_id: "", quantity: "1", related_event_id: "", ship_date: "", tracking_number: "", notes: "", reason: "" });
    queryClient.invalidateQueries({ queryKey: ["inventory-transfers"] });
  };

  const handleStatusChange = async (transfer: any, newStatus: string) => {
    const updates: any = { status: newStatus };
    if (newStatus === "received") {
      updates.received_date = new Date().toISOString();
      // Update warehouse inventory
      // Decrease from_warehouse
      const { data: fromInv } = await supabase
        .from("manage_warehouse_inventory")
        .select("id, quantity")
        .eq("warehouse_id", transfer.from_warehouse_id)
        .eq("item_id", transfer.item_id)
        .single();
      if (fromInv) {
        await supabase.from("manage_warehouse_inventory")
          .update({ quantity: Math.max(0, fromInv.quantity - transfer.quantity), updated_at: new Date().toISOString() })
          .eq("id", fromInv.id);
      }
      // Increase to_warehouse (upsert)
      const { data: toInv } = await supabase
        .from("manage_warehouse_inventory")
        .select("id, quantity")
        .eq("warehouse_id", transfer.to_warehouse_id)
        .eq("item_id", transfer.item_id)
        .single();
      if (toInv) {
        await supabase.from("manage_warehouse_inventory")
          .update({ quantity: toInv.quantity + transfer.quantity, updated_at: new Date().toISOString() })
          .eq("id", toInv.id);
      } else {
        await supabase.from("manage_warehouse_inventory").insert({
          warehouse_id: transfer.to_warehouse_id,
          item_id: transfer.item_id,
          quantity: transfer.quantity,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["warehouse-inventory"] });
    }

    const { error } = await supabase.from("manage_inventory_transfers").update(updates).eq("id", transfer.id);
    if (error) { toast.error("Failed to update status"); return; }
    toast.success(`Transfer marked as ${newStatus}`);
    queryClient.invalidateQueries({ queryKey: ["inventory-transfers"] });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      {/* Filters + Actions */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Warehouse</Label>
          <Select value={filterWarehouse} onValueChange={setFilterWarehouse}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Warehouses</SelectItem>
              {(warehouses || []).map((w: any) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" />New Transfer</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Transfer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Item *</Label>
                <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select item..." /></SelectTrigger>
                  <SelectContent>{(items || []).map((i: any) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>From Warehouse *</Label>
                <Select value={form.from_warehouse_id} onValueChange={(v) => setForm({ ...form, from_warehouse_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{(warehouses || []).map((w: any) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>To Warehouse *</Label>
                <Select value={form.to_warehouse_id} onValueChange={(v) => setForm({ ...form, to_warehouse_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{(warehouses || []).map((w: any) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Quantity</Label><Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
              <div><Label>Related Event</Label>
                <Select value={form.related_event_id} onValueChange={(v) => setForm({ ...form, related_event_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Optional..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">None</SelectItem>
                    {(events || []).map((e: any) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Ship Date</Label><Input type="date" value={form.ship_date} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} /></div>
              <div><Label>Tracking Number</Label><Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} /></div>
              <div><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <Button onClick={handleCreate} className="w-full">Create Transfer</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Transfers Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead className="text-center"><ArrowRightLeft className="h-4 w-4 mx-auto" /></TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>Ship Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tracking #</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No transfers found.</TableCell></TableRow>
                ) : filtered.map((t: any) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.item?.name || "—"}</TableCell>
                    <TableCell>{t.from_warehouse?.name || "—"}</TableCell>
                    <TableCell className="text-center">→</TableCell>
                    <TableCell>{t.to_warehouse?.name || "—"}</TableCell>
                    <TableCell className="text-center font-semibold">{t.quantity}</TableCell>
                    <TableCell>{t.ship_date ? format(new Date(t.ship_date), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell>
                      <Select value={t.status} onValueChange={(v) => handleStatusChange(t, v)}>
                        <SelectTrigger className="w-[120px]">
                          <Badge className={statusColors[t.status] || ""}>{t.status}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="received">Received</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm">{t.tracking_number || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">{t.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TransfersTab;
